import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, CheckCircle } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

export default function SubscribePage() {
  useSEO({
    title: 'Subscribe | Acodia — Grow. Defend. Be Found.',
    description: 'Choose your Acodia Watch subscription to unlock weekly scans and real-time alerts.',
    path: '/subscribe',
    robots: 'noindex,nofollow',
  });
  const { user } = useAuth();

  const goToActiveGuard = () => {
    const target = user?.email
      ? `/watch?email=${encodeURIComponent(user.email)}`
      : '/watch';
    window.location.href = target;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg)' }}>
      <div className="max-w-xl w-full">
        {/* Logo */}
        <a href="/" className="block text-center mb-8">
          <span className="font-display text-2xl font-bold" style={{ color: 'var(--white)' }}>acodia</span>
        </a>

        {/* Subscription Required Card */}
        <div 
          className="p-8 rounded-lg mb-6"
          style={{ 
            background: 'var(--l1)', 
            border: '1px solid var(--b1)',
          }}
        >
          <div className="text-center mb-6">
            <Shield size={48} style={{ color: 'var(--a)', margin: '0 auto 16px' }} />
            <h1 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--white)' }}>
              Acodia Watch Required
            </h1>
            <p className="font-body text-sm" style={{ color: 'var(--tm)' }}>
              Subscribe to Acodia Watch to access the Client Pulse Dashboard and start monitoring your domains.
            </p>
          </div>

          {user && (
            <div className="mb-6 p-3 rounded" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
              <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                Logged in as <span className="font-medium" style={{ color: 'var(--white)' }}>{user.email}</span>
              </p>
            </div>
          )}

          {/* Plan Details */}
          <div className="mb-6">
            <div className="mb-4">
              <div className="font-display text-2xl font-extrabold" style={{ color: 'var(--white)' }}>
                Plans from £49<span className="font-body text-base font-normal" style={{ color: 'var(--ts)' }}>/month</span>
              </div>
              <p className="font-body text-xs mt-1" style={{ color: 'var(--ts)' }}>
                Essential · Professional · Business
              </p>
            </div>

            <ul className="space-y-3 mb-6">
              {[
                'Continuous security monitoring',
                'Weekly security intelligence briefing',
                'Real-time alerts on critical findings',
                'Full scan history and reporting',
                'TLS certificate and header analysis',
                'CVE vulnerability tracking',
                'Client Pulse Dashboard access',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 font-body text-sm" style={{ color: 'var(--tm)' }}>
                  <CheckCircle size={16} style={{ color: 'var(--ok)', marginTop: '2px', flexShrink: 0 }} />
                  {feature}
                </li>
              ))}
            </ul>

            <Button
              data-testid="subscribe-button"
              onClick={goToActiveGuard}
              className="w-full btn-shimmer font-body font-medium text-sm h-12 rounded-[3px]"
              style={{ background: 'var(--a)', color: '#fff' }}
            >
              Subscribe to Acodia Watch
              <ArrowRight size={16} className="ml-2" />
            </Button>

            <p className="font-body text-xs text-center mt-3" style={{ color: 'var(--te)' }}>
              Cancel anytime. No long-term commitment.
            </p>
          </div>
        </div>

        <p className="text-center font-body text-xs" style={{ color: 'var(--te)' }}>
          By subscribing you agree to our <a href="/terms" className="underline">Terms of Service</a> and <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
